import React, { useMemo } from "react";
import { Box, Typography, CardContent, CardMedia, Button, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { foodImageSource } from "src/config/foodImage";
import itemImageFallback from "src/assets/categories/snacks.webp";

function FoodCardContent({
  fooditem,
  subcategory,
  handleAddToCart,
  item_image,
  image,
}) {
  const navigate = useNavigate();

  const optimizedImage = useMemo(
    () => foodImageSource(image || item_image) || itemImageFallback,
    [image, item_image],
  );

  const price = useMemo(() => {
    const hour = new Date().getHours();
    const isDay = hour >= 6 && hour < 18;

    try {
      const prices = JSON.parse(fooditem.item_price || "[]");
      if (prices.length > 1) return isDay ? prices[0] : prices[1];
      if (prices.length === 1) return prices[0];
      return "--";
    } catch {
      return "--";
    }
  }, [fooditem.item_price]);

  const handleDetailsClick = () => {
    navigate("/description", {
      state: {
        foodItem: { ...fooditem, image: optimizedImage },
        subcategory,
      },
    });
  };

  return (
    <CardContent
      sx={{
        width: "100%",
        p: 0,
        pt: 2,
        boxSizing: "border-box",
        "&:last-child": {
          paddingBottom: 0,
        },
      }}
    >
      <CardMedia
        component="img"
        src={optimizedImage}
        alt={[fooditem.item_subcategory, fooditem.item_name].filter(Boolean).join(" ") || "Food item"}
        width="160"
        height="160"
        loading="lazy"
        decoding="async"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = itemImageFallback;
        }}
        sx={{
          width: { xs: 120, sm: 160 },
          height: { xs: 120, sm: 160 },
          aspectRatio: "1 / 1",
          mx: "auto",
          mt: 0,
          mb: 1,
          objectFit: "cover",
          objectPosition: "center",
          display: "block",
          borderRadius: "50%",
          border: 0,
          backgroundColor: "#eee9dc",
          mixBlendMode: "multiply",
          filter: "drop-shadow(0 8px 12px rgba(65,70,39,.16))",
        }}
      />

      <Box sx={{ px: { xs: 1.5, sm: 2.25 }, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography
            sx={{
              color: "#252920",
              fontFamily: "Georgia, serif",
              fontSize: { xs: "0.98rem", sm: "1.12rem" },
              fontWeight: 700,
              lineHeight: 1.15,
              textTransform: "capitalize",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fooditem.item_name || subcategory}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ color: "#b58519", fontFamily: "Georgia, serif", fontSize: { xs: "0.85rem", sm: "1rem" }, whiteSpace: "nowrap" }}>
              Rs {price}
            </Typography>
          </Box>
        </Box>

        {(
          <Typography sx={{ color: "#747767", fontSize: { xs: "0.75rem", sm: "0.84rem" }, mt: 0.5, height: 20, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", textTransform: "capitalize" }}>
            {[fooditem.item_subcategory, fooditem.item_type]
              .map((value) => typeof value === "string" ? value.trim() : "")
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}

        <Box sx={{ height: "1px", my: 0.75, background: "linear-gradient(90deg, #e2ad30, #e2dfcf)" }} />

        <Typography
          sx={{
            color: "#747767",
            fontSize: { xs: "0.75rem", sm: "0.84rem" },
            lineHeight: 1.5,
            height: { xs: 36, sm: 40 },
            textAlign: "left",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {fooditem.item_description || "Freshly prepared with rich, comforting flavors."}
        </Typography>

        <Typography sx={{ color: "#747767", fontSize: "0.68rem", mt: 0.75, textAlign: "center" }}>
          Freshly made <span style={{ color: "#e2ad30", padding: "0 5px" }}>•</span> Serves 4
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1.5, sm: 2.25 },
          pb: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleDetailsClick}
          startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            minHeight: { xs: 34, sm: 38 },
            px: 1,
            borderRadius: "8px",
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            borderColor: "#e2dfcf",
            color: "#747767",
            textTransform: "capitalize",
            lineHeight: 1,

            /* 🔥 ICON GAP CONTROL */
            "& .MuiButton-startIcon": {
              marginRight: { xs: "4px", sm: "8px" },
              marginLeft: 0,
            },
          }}
        >
          Details
        </Button>

        <Tooltip title="Add to cart">
          <IconButton
          onClick={handleAddToCart}
          sx={{
            width: { xs: 34, sm: 38 },
            height: { xs: 34, sm: 38 },
            borderRadius: "10px",
            background: "#e2ad30",
            color: "#252920",
            "&:hover": {
              background: "#b58519",
            },
          }}
          aria-label="Add to cart"
          >
            <AddIcon sx={{ fontSize: { xs: 19, sm: 22 } }} />
          </IconButton>
        </Tooltip>
      </Box>
    </CardContent>
  );
}

FoodCardContent.propTypes = {
  fooditem: PropTypes.object.isRequired,
  subcategory: PropTypes.string.isRequired,
  handleAddToCart: PropTypes.func.isRequired,
  item_image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default FoodCardContent;
